import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'

const EMBEDDED_MESSAGING_SCRIPT_ID = 'salesforce-embedded-messaging-bootstrap'
const EMBEDDED_MESSAGING_SELECTORS = [
  '#embeddedMessagingFrame',
  '#embeddedMessagingConversationButton',
  '#embeddedMessagingModalOverlay',
  '[id*="embeddedMessaging"]',
  '[class*="embeddedMessaging"]',
]

const removeEmbeddedMessagingElements = () => {
  document
    .querySelectorAll(EMBEDDED_MESSAGING_SELECTORS.join(','))
    .forEach(element => element.remove())
}

const resetEmbeddedMessaging = async () => {
  const embeddedMessagingBootstrap = window.embeddedservice_bootstrap

  if (!embeddedMessagingBootstrap) {
    removeEmbeddedMessagingElements()
    return
  }

  try {
    await embeddedMessagingBootstrap.userVerificationAPI?.clearSession(true)
  } catch (err) {
    console.error('Error clearing Embedded Messaging session: ', err)
  }

  if (!embeddedMessagingBootstrap.utilAPI?.removeAllComponents) {
    removeEmbeddedMessagingElements()
    return
  }

  try {
    await embeddedMessagingBootstrap.utilAPI.removeAllComponents()
  } catch (err) {
    console.error('Error removing Embedded Messaging components: ', err)
    removeEmbeddedMessagingElements()
  }
}

const getEmbeddedMessagingConfig = (language: string) => {
  const {
    REACT_APP_EMBEDDED_MESSAGING_ORG_ID,
    REACT_APP_EMBEDDED_MESSAGING_DEVELOPER_NAME,
    REACT_APP_EMBEDDED_MESSAGING_SITE_URL,
    REACT_APP_EMBEDDED_MESSAGING_SCRT_URL,
  } = process.env

  if (
    !REACT_APP_EMBEDDED_MESSAGING_ORG_ID ||
    !REACT_APP_EMBEDDED_MESSAGING_DEVELOPER_NAME ||
    !REACT_APP_EMBEDDED_MESSAGING_SITE_URL ||
    !REACT_APP_EMBEDDED_MESSAGING_SCRT_URL
  ) {
    return null
  }
  const allowedLanguages = ['en-US', 'fr', 'de', 'es', 'it']
  const realLanguage = allowedLanguages.includes(language) ? language : 'en-US'

  return {
    language: realLanguage,
    orgId: REACT_APP_EMBEDDED_MESSAGING_ORG_ID,
    developerName: REACT_APP_EMBEDDED_MESSAGING_DEVELOPER_NAME,
    siteUrl: REACT_APP_EMBEDDED_MESSAGING_SITE_URL,
    scrtUrl: REACT_APP_EMBEDDED_MESSAGING_SCRT_URL,
  }
}

interface IEmbeddedMessagingBootstrap {
  settings: {
    language: string
    useHostSiteLanguage?: boolean
  }
  userVerificationAPI?: {
    clearSession: (shouldEndSession: boolean) => Promise<void>
  }
  utilAPI?: {
    removeAllComponents: () => Promise<void> | void
  }
  init: (
    orgId: string,
    developerName: string,
    siteUrl: string,
    options: {
      scrt2URL: string
    },
  ) => void
}

declare global {
  interface Window {
    embeddedservice_bootstrap?: IEmbeddedMessagingBootstrap
    initEmbeddedMessaging?: () => void
  }
}

const LiveChat2 = () => {
  const { i18n } = useTranslation()
  const language = i18n.language
  const hasInitializedEmbeddedMessagingRef = useRef(false)
  const initializationIdRef = useRef(0)

  useEffect(() => {
    const embeddedMessagingConfig = getEmbeddedMessagingConfig(language)

    if (!embeddedMessagingConfig) {
      return
    }

    const initializationId = initializationIdRef.current + 1
    initializationIdRef.current = initializationId

    const initializeEmbeddedMessaging = () => {
      try {
        if (initializationIdRef.current !== initializationId) {
          return
        }

        if (!window.embeddedservice_bootstrap) {
          return
        }

        window.embeddedservice_bootstrap.settings.language =
          embeddedMessagingConfig.language
        window.embeddedservice_bootstrap.settings.useHostSiteLanguage = true
        window.embeddedservice_bootstrap.init(
          embeddedMessagingConfig.orgId,
          embeddedMessagingConfig.developerName,
          embeddedMessagingConfig.siteUrl,
          {
            scrt2URL: embeddedMessagingConfig.scrtUrl,
          },
        )
        hasInitializedEmbeddedMessagingRef.current = true
      } catch (err) {
        console.error('Error loading Embedded Messaging: ', err)
      }
    }

    const initializeCurrentLanguage = async () => {
      if (hasInitializedEmbeddedMessagingRef.current) {
        await resetEmbeddedMessaging()
        hasInitializedEmbeddedMessagingRef.current = false
      }

      if (initializationIdRef.current !== initializationId) {
        return
      }

      window.initEmbeddedMessaging = initializeEmbeddedMessaging

      const existingScript = document.getElementById(
        EMBEDDED_MESSAGING_SCRIPT_ID,
      ) as HTMLScriptElement | null

      if (existingScript) {
        existingScript.onload = initializeEmbeddedMessaging

        if (window.embeddedservice_bootstrap) {
          initializeEmbeddedMessaging()
        }

        return
      }

      const script = document.createElement('script')
      script.id = EMBEDDED_MESSAGING_SCRIPT_ID
      script.type = 'text/javascript'
      script.src = `${embeddedMessagingConfig.siteUrl}/assets/js/bootstrap.min.js`
      script.onload = initializeEmbeddedMessaging

      document.body.appendChild(script)
    }

    initializeCurrentLanguage()

    return () => {
      if (initializationIdRef.current === initializationId) {
        initializationIdRef.current += 1
      }
    }
  }, [language])

  useEffect(() => {
    return () => {
      const script = document.getElementById(
        EMBEDDED_MESSAGING_SCRIPT_ID,
      ) as HTMLScriptElement | null

      if (script) {
        script.onload = null
        script.remove()
      }

      resetEmbeddedMessaging()
      removeEmbeddedMessagingElements()
      delete window.embeddedservice_bootstrap
      delete window.initEmbeddedMessaging
    }
  }, [])

  return null
}

export default LiveChat2
